import { useEffect, useState } from 'react';

/**
 * Viewport de celular, no mesmo ponto de corte do Tailwind (`md` = 768px).
 *
 * Existe porque nem todo caso de mobile se resolve no CSS. Esconder um modulo
 * pesado com `max-md:hidden` continuaria MONTANDO o componente: ele buscaria
 * dados, assinaria realtime e desenharia fora da tela, gastando bateria e rede
 * de quem nunca veria o resultado. Para "nao renderizar", a decisao tem que
 * chegar ao React.
 *
 * Para tudo que e so aparencia, a regra continua sendo `max-md:` no CSS: e mais
 * barato, nao re-renderiza e nao pisca no primeiro quadro.
 *
 * O valor inicial le o `matchMedia` de forma sincrona, e nao `false`: comecar
 * errado faria a tela montar o modulo de desktop por um quadro antes de trocar,
 * que e exatamente o custo que este hook existe para evitar.
 */
const CONSULTA = '(max-width: 767px)';

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(CONSULTA).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(CONSULTA);
    const aoMudar = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', aoMudar);
    // Girar o aparelho entre o primeiro render e o efeito e raro, mas custa uma
    // linha cobrir.
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', aoMudar);
  }, []);

  return isMobile;
}

/**
 * Existe ponteiro fino de verdade (mouse ou trackpad)?
 *
 * SEGUNDO EIXO, deliberadamente separado da largura. `useIsMobile` responde
 * "que tamanho de tela e este"; este responde "com o que a pessoa aponta". Os
 * dois nao sao intercambiaveis, e confundi-los custou um defeito real:
 *
 * Em 25/08/2026 a barra de filtros do Kanban virou uma folha de baixo abaixo de
 * 768px, para acabar com a rolagem lateral de barra escondida (affordancia que
 * ninguem ve nao e affordancia). Mas entre 768 e 1023px os quatro seletores
 * continuaram em rolagem escondida, e essa faixa e iPad em retrato e tablet
 * Android: TOQUE, sem mouse nenhum. O defeito nao foi corrigido, foi movido um
 * degrau acima.
 *
 * A pergunta certa nunca foi "a tela e pequena?", e sim "da para apontar com
 * precisao e existe hover?". Onde nao da, o controle dificil vira folha.
 *
 * Espelha a variante `mouse:` do tailwind.config.js: mesma media query, um lado
 * em CSS e outro em React, para nunca discordarem.
 */
const CONSULTA_MOUSE = '(hover: hover) and (pointer: fine)';

export function useTemMouse(): boolean {
  const [temMouse, setTemMouse] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia(CONSULTA_MOUSE).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(CONSULTA_MOUSE);
    const aoMudar = (e: MediaQueryListEvent) => setTemMouse(e.matches);
    mql.addEventListener('change', aoMudar);
    // Plugar um mouse num tablet muda a resposta sem recarregar a pagina.
    setTemMouse(mql.matches);
    return () => mql.removeEventListener('change', aoMudar);
  }, []);

  return temMouse;
}
